import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import estrategiaService, { Estrategia } from '../../services/estrategiaService';

const defaultForm: Estrategia = {
  nint: 0,
  nombre: '',
  tipo: 'C',
  activo: 'S',
  descripcion: ''
};

const tiposEstrategia = [
  { value: 'C', label: 'Compras' },
  { value: 'V', label: 'Ventas' },
  { value: 'T', label: 'Tesorería' },
  { value: 'O', label: 'Otros' }
];

const EstrategiaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nint } = useParams();
  const [formData, setFormData] = useState<Estrategia>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const loadEstrategia = useCallback(async () => {
    if (!nint || nint === 'nuevo') return;
    try {
      setLoading(true);
      const estrategia = await estrategiaService.getEstrategia(Number(nint));
      setFormData(estrategia);
    } catch (error) {
      enqueueSnackbar('Error al cargar la estrategia', { variant: 'error' });
      navigate('/contabilidad/estrategia');
    } finally {
      setLoading(false);
    }
  }, [nint, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nint && nint !== 'nuevo') loadEstrategia();
  }, [nint, loadEstrategia]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nint') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [name]: e.target.checked ? 'S' : 'N' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombre.trim()) {
        enqueueSnackbar('El nombre de la estrategia es requerido', { variant: 'warning' });
        return;
      }

      if (nint && nint !== 'nuevo') {
        await estrategiaService.updateEstrategia(Number(nint), formData);
      } else {
        await estrategiaService.createEstrategia(formData);
      }

      enqueueSnackbar('Estrategia guardada exitosamente', { variant: 'success' });
      navigate('/contabilidad/estrategia');
    } catch (error: any) {
      console.error('Error al guardar estrategia:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la estrategia', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nint && nint !== 'nuevo' ? 'Editar Estrategia' : 'Nueva Estrategia'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="nint" 
                    label="N° Interno" 
                    type="number"
                    value={formData.nint} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(nint && nint !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField 
                    name="nombre" 
                    label="Nombre" 
                    value={formData.nombre} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="descripcion" 
                    label="Descripción" 
                    value={formData.descripcion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      name="tipo"
                      value={formData.tipo}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                      label="Tipo"
                      sx={{ borderRadius: 2 }}
                    >
                      {tiposEstrategia.map(tipo => (
                        <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={formData.activo === 'S'} onChange={handleSwitchChange('activo')} />}
                    label="Activo"
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/contabilidad/estrategia')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EstrategiaForm;
